import 'reflect-metadata';

import {
  IAccreditationsConfig,
  ISearchFilter,
  ISearchResults,
} from '@orcha/types';

import IAppCardAppData from '@orcha/types/app-card-app-data';
import IAppData from '@orcha/types/app-data';
import IDataPagination from '@orcha/types/data-pagination';
import { ISearchSuggestion } from '@orcha/types/search';
import { injectable } from 'inversify';
import { mapToAppCardData } from '@orcha/utils';
import { ISearchService } from './interfaces';
import ApiClient from './api-client';

@injectable()
class SearchService implements ISearchService {
  private apiClient: ApiClient;

  public constructor() {
    this.apiClient = new ApiClient(process.env.APPS_API_BASE_URL ?? '');
  }

  public async getSearchResults(
    tenantId: string,
    packageId: string,
    containerId: string,
    searchQuery?: object,
    accreditationsConfig?: IAccreditationsConfig
  ): Promise<ISearchResults | undefined> {
    let queryString: string | undefined;

    if (searchQuery) {
      queryString = `?${Object.keys(searchQuery)
        .filter((key) => !!(searchQuery as any)[key])
        .map((key) => {
          const value = (searchQuery as any)[key];
          const isArray = Array.isArray(value);
          if (isArray) {
            return value
              .map((v) => `${key}=${encodeURIComponent(v)}`)
              .join('&');
          }

          return `${key}=${encodeURIComponent(value)}`;
        })
        .join('&')}`;
    }

    const response = await this.apiClient.get<ISearchResultsDataWrapper>(
      `v1/tenants/${tenantId}/packages/${packageId}/orchaplatforms/appLibrary/containers/${containerId}/apps${
        queryString || ''
      }`
    );

    if (!response.data) {
      return undefined;
    }

    const appCardsAppData = response.data?.apps.map((app) => {
      return mapToAppCardData(app, accreditationsConfig);
    });

    return { apps: appCardsAppData, paging: response.data?.paging };
  }

  public async getSearchFilterOptions(
    tenantId: string,
    packageId: string,
    containerId: string,
    language?: string,
    country?: string,
    includeAllCommissionableApps?: string
  ): Promise<ISearchFilter[]> {
    const languageQueryString = language
      ? `&language=${language.toLocaleLowerCase()}`
      : '';
    const countryQueryString = country ? `&country=${country}` : '';
    const includeAllCommissionableAppsQueryString = includeAllCommissionableApps
      ? `&includeAllCommissionableApps=${includeAllCommissionableApps}`
      : '';

    const response = await this.apiClient.get<ISearchFiltersDataWrapper>(
      `v2/tenants/${tenantId}/packages/${packageId}/orchaplatforms/appLibrary/containers/${containerId}/filters?${languageQueryString}${countryQueryString}${includeAllCommissionableAppsQueryString}`
    );

    let data: ISearchFilter[] = [];
    if (response.data) {
      data = response.data.filters;
    }
    return data;
  }

  public async getAppById(
    tenantId: string,
    packageId: string,
    containerId: string,
    appId: string,
    language?: string,
    country?: string,
    accreditationsConfig?: IAccreditationsConfig
  ): Promise<IAppCardAppData | undefined> {
    const countryQueryString = country ? `&country=${country}` : '';
    const languageQueryString = language
      ? `&language=${language.toLocaleLowerCase()}`
      : '';

    const response = await this.apiClient.get<ISearchResultDataWrapper>(
      `v1/tenants/${tenantId}/packages/${packageId}/orchaplatforms/appLibrary/containers/${containerId}/apps/${appId}?${languageQueryString}${countryQueryString}`
    );

    if (!response.data) {
      return undefined;
    }

    const appCardAppData = mapToAppCardData(
      response.data?.app,
      accreditationsConfig
    );

    return appCardAppData;
  }

  public async getSearchSuggestionOptions(
    tenantId: string,
    packageId: string,
    containerId: string,
    searchTerm: string,
    language?: string,
    country?: string
  ): Promise<ISearchSuggestion[]> {
    const languageQueryString = language
      ? `&language=${language.toLocaleLowerCase()}`
      : '';
    const countryQueryString = country ? `&country=${country}` : '';

    const response = await this.apiClient.get<ISearchSuggestionsResponse>(
      `v1/tenants/${tenantId}/packages/${packageId}/containers/${containerId}/search-suggestions?searchTerm=${searchTerm}${languageQueryString}${countryQueryString}`
    );

    let data: ISearchSuggestion[] = [];

    if (response.data) {
      data = response.data.suggestions;
    }

    return data;
  }
}

interface ISearchResultDataWrapper {
  data?: { app: IAppData };
  status: string;
}

interface ISearchResultsDataWrapper {
  data?: { apps: IAppData[]; paging: IDataPagination };
  status: string;
}

interface ISearchFiltersDataWrapper {
  data?: { filters: ISearchFilter[] };
  status: string;
}

interface IAutocompleteResponse {
  data?: { suggestions: string[] };
  status: string;
}

interface ISearchSuggestionsResponse {
  data?: { suggestions: ISearchSuggestion[] };
  status: string;
}

export default SearchService;
